import { Box, Dialog, DialogActions, DialogContent, FormHelperText } from '@mui/material';
import Grid from '@mui/material/Grid';
import React, { useEffect } from 'react';
import { CustomDialogTitle } from '../../../../Common/Custom/CustomDialogTitle';
import { MDBox } from '../../../../../Component/Common/Components/MDBox';
import { Controller, FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MDButton } from '../../../../../Component/Common/Components/MDButton';
import { Deviation } from '../../../../../Component/Model/Dto/EmploymentEquity/Plan/Deviation';
import { DeviationsService } from '../../../../../Component/Api/EmploymentEquity/WorkforceProfileGoalsAndTargets/DeviationsService';
import { EEOccupationalLevel } from '../../../../../Component/Model/Enum/EmploymentEquity';
import { Gender, RaceTypes } from '../../../../../Component/Model/Enum/BEE123Enums';
import { CoreStaticDataService } from '../../../../../Component/Api/StaticData/CoreStaticDataService';
import { MDTypography } from '../../../../../Component/Common/Components/MDTypography';
import { deviationSchema } from './DeviationValidation';
import { SmallLoading } from '../../../../../Component/Common/Components/Loading/IntermediateLoading';
import AppSelectList, { IDropDownOptions } from '../../../../../Component/Common/Components/FormValidationInputs/AppSelectList';
import { PortalDocumentsDropZone } from '../../../../Common/PortalDocuments/PortalDocumentsDropZone';
import { ConfirmationDialog } from '../../../../../Component/Common/Components/ConfirmationDialog';
import { toast } from 'react-toastify';
import { Quill } from "../../../../../Component/Common/Components/Quill/Quill";

interface IProps {
	id?: number;
	occupationalLevel: EEOccupationalLevel;
	gender?: Gender;
	race?: RaceTypes;
	isDisabled: boolean;
	year: number;
	scorecardId: string;
	showModal: boolean;
	handleCancel: () => void;
}

export const DeviationModal = (props: IProps) => {
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [isDeleting, setIsDeleting] = React.useState<boolean>(false);
	const [deviationId, setDeviationId] = React.useState<number | undefined>(props.id);
	const [deviationReasons, setDeviationReasons] = React.useState<IDropDownOptions[]>([]);
	const [showDeleteConfirmationDialog, setShowDeleteConfirmationDialog] = React.useState<boolean>(false);
    const commentMaxLength = 6000;
    const [remainingCommentCharacters, setRemainingCommentCharacters] = React.useState(commentMaxLength);

	const { handleSubmit, control, reset, formState: { errors }, setValue } = useForm({
		mode: 'all',
		resolver: yupResolver(deviationSchema)
	});

	const getDeviationReasons = async () => {
		try {
			const result = await CoreStaticDataService.getDeviationReasons();
			setDeviationReasons(
				result.data.map((item) => {
					return { id: item.id, value: item.name };
				})
			);
		} catch (error) {
			console.log(error);
		}
	};

	const getDeviation = async (id: number) => {
		try {
			let result = await DeviationsService.GetDeviation(id);
			reset(result.data as Deviation.Dto);
		} catch (error) {
			console.log(error);
		}
	};

	const deleteDeviation = async () => {
		setIsDeleting(true);
		try {
			if (deviationId) {
				await DeviationsService.DeleteDeviation(deviationId);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsDeleting(false);
			handleCloseConfirmation();
			props.handleCancel();
		}
	};

	const handleDataSubmit: SubmitHandler<FieldValues> = async (data: FieldValues) => {
		setIsLoading(true);
		try {
			const deviation: Deviation.Dto = {
				eeOccupationalLevel: props.occupationalLevel,
				gender: props.gender,
				race: props.race,
				scorecardId: props.scorecardId,
				reason: data.reason,
				comment: data.comment,
				isDisabled: props.isDisabled,
				goalAndTargetRowYear: props.year
			};
			if (deviationId == undefined) {
				const result = await DeviationsService.CreateDeviation(deviation);
				reset(result.data);
				setDeviationId(result.data.id);
				toast.success('Deviation created successfully');
			} else {
				deviation.id = deviationId;
				const result = await DeviationsService.UpdateDeviation(deviation);
				reset(result.data);
				toast.success('Deviation updated successfully');
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleDelete = () => {
		setShowDeleteConfirmationDialog(true);
	};

	const handleCloseConfirmation = () => {
		setShowDeleteConfirmationDialog(false);
	};

	useEffect(() => {
		setIsLoading(true);
		getDeviationReasons();
		if (deviationId != undefined) {
			getDeviation(deviationId);
		}
		setIsLoading(false);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deviationId]);

	return (
		<>
			<Dialog open={props.showModal} onClose={props.handleCancel} aria-labelledby='form-dialog-title' maxWidth='md' fullWidth>
				<CustomDialogTitle id='customized-dialog-title' onClose={props.handleCancel}>
					{deviationId == undefined ? 'Add' : 'Edit'} {'Deviation'}
				</CustomDialogTitle>
				<DialogContent>
					{isLoading ? (
						<SmallLoading />
					) : (
						<Box component='form' id='deviationsForm' onSubmit={handleSubmit(handleDataSubmit)}>
							<Grid container>
								<Grid item xs={12} xl={12}>
									<MDBox>
										<MDBox mb={2} mt={2}>
											<AppSelectList
												control={control}
												name='reason'
												label='Justifiable Reason*'
												items={deviationReasons}
												size={'small'}
											/>
										</MDBox>
										<MDBox>
											<MDTypography variant='button' fontWeight='regular' style={{ color: '#666666', fontSize: '0.85em' }}>
												Comment (Max 6000 characters)
											</MDTypography>
											<Controller
												control={control}
												name='comment'
												render={({ field: { onChange, value } }) => (
													<Quill
														initialContent={value}
														maxLength={commentMaxLength}
														handleChange={(newValue: string) => {
															onChange(newValue);
															setValue('comment', newValue);
														}}
														setRemainingCharacters={setRemainingCommentCharacters}
													/>
												)}
											/>
											<MDTypography
												variant='button'
												fontWeight='regular'
												style={{ color: remainingCommentCharacters < 0 ? '#FF0000' : '#666666', fontSize: '0.85em' }}
											>
												{remainingCommentCharacters} characters remaining
											</MDTypography>
											{!!errors.comment && <FormHelperText error>{errors.comment?.message}</FormHelperText>}
										</MDBox>
									</MDBox>
								</Grid>
							</Grid>
							{deviationId != undefined && (
								<MDBox mt={2}>
									<PortalDocumentsDropZone id={deviationId} link={'deviation'} />
								</MDBox>
							)}
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					{!isLoading && (
						<DialogActions
							sx={{
								display: 'flex',
								justifyContent: deviationId != undefined ? 'space-between' : '',
								width: '100%',
								paddingLeft: 0,
								paddingRight: 0
							}}
						>
							{deviationId != undefined && (
								<MDButton color='error' variant='outlined' onClick={handleDelete} sx={{ marginRight: 'auto' }}>
									Delete
								</MDButton>
							)}
							<MDButton color='primary' variant='contained' type='submit' form='deviationsForm'>
								Save
							</MDButton>
						</DialogActions>
					)}
				</DialogActions>
			</Dialog>
			<ConfirmationDialog
				showDialog={showDeleteConfirmationDialog}
				handleAccept={deleteDeviation}
				handleCancel={handleCloseConfirmation}
				heading={'Delete Deviation'}
				message={'Are you sure you want to delete this deviation and associated files?'}
				isLoading={isDeleting}
			/>
		</>
	);
};
